/**
 * PortfolioHealthScoreBadge — Round 96n (Jul 3 2026)
 *
 * Single 0–100 grade for a portfolio, computed client-side from data
 * already available on the Portfolio Contrast row. Sits at the top
 * of the "Conviction Snapshot" section inside PortfolioHealthSnapshot.
 *
 * Five equally-weighted sub-scores (20 points each, max 100):
 *
 *   (a) Real Yield ≥ 0 — nominal portfolio yield minus CPI YoY.
 *       Positive = 20; scales linearly to 0 at −4% real yield.
 *   (b) FCF Coverage ≥ 1.5× — weighted dividend / free-cash-flow
 *       coverage. ≥1.5× = 20; ≥1.0× = 10; ≥0.5× = 5; else 0.
 *   (c) Revenue-growth blend (Rule-of-40 proxy) — weighted revenue
 *       YoY across income contributors. ≥ +5% = 20; ≥ 0% = 12;
 *       ≥ −3% = 6; else 0.
 *   (d) Income concentration < 25% — largest single-ticker share.
 *       ≤25% = 20; linearly to 0 at 50%.
 *   (e) Streak / walk-away exposure — walk-away positions out of
 *       total analysed. 0% = 20; linearly to 0 at 50%.
 *
 * Verdict bands:
 *   85–100 = Excellent (emerald)
 *   70–84  = Solid (emerald/lime)
 *   50–69  = Mixed (amber)
 *   <50    = Weak (rose)
 *
 * Renders nothing when we don't have enough data to compute at least
 * three of the five sub-scores (thin new portfolios shouldn't get
 * shamed by a partial grade).
 */
import { useState } from 'react';
import { ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { computeRealYield } from '@/lib/realYield';

function toneForScore(score) {
  if (score >= 85) return {
    ring: 'ring-emerald-500/60',
    text: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-50/70 dark:bg-emerald-950/40',
    verdict: 'Excellent',
    verdictColor: 'text-emerald-800 dark:text-emerald-200',
  };
  if (score >= 70) return {
    ring: 'ring-lime-500/60',
    text: 'text-lime-700 dark:text-lime-300',
    bg: 'bg-lime-50/70 dark:bg-lime-950/40',
    verdict: 'Solid',
    verdictColor: 'text-lime-800 dark:text-lime-200',
  };
  if (score >= 50) return {
    ring: 'ring-amber-500/60',
    text: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50/70 dark:bg-amber-950/40',
    verdict: 'Mixed',
    verdictColor: 'text-amber-800 dark:text-amber-200',
  };
  return {
    ring: 'ring-rose-500/60',
    text: 'text-rose-700 dark:text-rose-300',
    bg: 'bg-rose-50/70 dark:bg-rose-950/40',
    verdict: 'Weak',
    verdictColor: 'text-rose-800 dark:text-rose-200',
  };
}

function scoreRealYield(nominalYield, cpiYoy) {
  if (typeof nominalYield !== 'number' || typeof cpiYoy !== 'number') return null;
  const real = computeRealYield(nominalYield, cpiYoy);
  if (real == null) return null;
  if (real >= 0) return { score: 20, real, tip: `Real yield ${real.toFixed(2)}% (Fisher-exact) — beating CPI ${cpiYoy.toFixed(2)}%.` };
  const pts = Math.max(0, 20 + real * 5); // −4% real = 0 pts
  return { score: pts, real, tip: `Real yield ${real.toFixed(2)}% (Fisher-exact) — losing purchasing power to CPI ${cpiYoy.toFixed(2)}%.` };
}
function scoreFcfCoverage(coverage) {
  if (typeof coverage !== 'number') return null;
  if (coverage >= 1.5) return { score: 20, tip: `Weighted FCF coverage ${coverage.toFixed(2)}× — comfortable cushion.` };
  if (coverage >= 1.0) return { score: 10, tip: `Weighted FCF coverage ${coverage.toFixed(2)}× — adequate but watch closely.` };
  if (coverage >= 0.5) return { score: 5, tip: `Weighted FCF coverage ${coverage.toFixed(2)}× — dividends topped up from cash reserves.` };
  return { score: 0, tip: `Weighted FCF coverage ${coverage.toFixed(2)}× — unsustainable.` };
}
function scoreRevGrowth(revYoY) {
  if (typeof revYoY !== 'number') return null;
  const pct = revYoY * 100;
  if (pct >= 5) return { score: 20, tip: `Weighted revenue growth +${pct.toFixed(1)}%/yr — Rule-of-40 healthy side.` };
  if (pct >= 0) return { score: 12, tip: `Weighted revenue growth +${pct.toFixed(1)}%/yr — flat but not shrinking.` };
  if (pct >= -3) return { score: 6, tip: `Weighted revenue growth ${pct.toFixed(1)}%/yr — mild contraction.` };
  return { score: 0, tip: `Weighted revenue growth ${pct.toFixed(1)}%/yr — meaningful contraction.` };
}
function scoreConcentration(concPct) {
  if (typeof concPct !== 'number') return null;
  if (concPct <= 25) return { score: 20, tip: `Largest position ${concPct.toFixed(1)}% — diversified.` };
  const pts = Math.max(0, 20 - ((concPct - 25) / 25) * 20);
  return { score: pts, tip: `Largest position ${concPct.toFixed(1)}% — over the 25% single-name threshold.` };
}
function scoreWalkAways(walkAways, totalAnalyzed) {
  if (typeof walkAways !== 'number' || !totalAnalyzed) return null;
  const ratio = walkAways / totalAnalyzed;
  if (ratio === 0) return { score: 20, tip: `Zero walk-away positions in ${totalAnalyzed} analysed.` };
  const pts = Math.max(0, 20 - (ratio / 0.5) * 20); // 50%+ walk-away = 0 pts
  return { score: pts, tip: `${walkAways} of ${totalAnalyzed} analysed positions are walk-away candidates.` };
}

export default function PortfolioHealthScoreBadge({
  portfolioYield,
  cpiYoy,
  fcfCoverage,
  revenueYoY,
  concentrationPct,
  walkAways,
  totalAnalyzed,
  testIdPrefix,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const parts = {
    realYield: scoreRealYield(portfolioYield, cpiYoy),
    fcfCoverage: scoreFcfCoverage(fcfCoverage),
    revGrowth: scoreRevGrowth(revenueYoY),
    concentration: scoreConcentration(concentrationPct),
    walkAways: scoreWalkAways(walkAways, totalAnalyzed),
  };
  const available = Object.values(parts).filter((p) => p !== null);
  // Need at least 3 signals for a fair grade — thin new portfolios
  // shouldn't get slapped with a partial score.
  if (available.length < 3) return null;
  const rawSum = available.reduce((s, p) => s + p.score, 0);
  const maxPossible = available.length * 20;
  const score = Math.round((rawSum / maxPossible) * 100);
  const tone = toneForScore(score);

  const rows = [
    { key: 'realYield', label: 'Real Yield ≥ 0', part: parts.realYield },
    { key: 'fcfCoverage', label: 'FCF Coverage ≥ 1.5×', part: parts.fcfCoverage },
    { key: 'revGrowth', label: 'Revenue Growth', part: parts.revGrowth },
    { key: 'concentration', label: 'Concentration < 25%', part: parts.concentration },
    { key: 'walkAways', label: 'Walk-Away Positions', part: parts.walkAways },
  ];

  return (
    <div
      className={`mb-2 rounded-md border border-border/50 ${tone.bg} px-2.5 py-2`}
      data-testid={`${testIdPrefix}-health-score`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className={`w-3.5 h-3.5 shrink-0 ${tone.text}`} />
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Portfolio Health Score
            </div>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className={`font-mono font-bold text-xl ${tone.text}`} data-testid={`${testIdPrefix}-health-score-value`}>
                {score}
              </span>
              <span className="text-[10px] font-mono text-muted-foreground">/ 100</span>
              <span className={`text-[11px] font-bold ${tone.verdictColor}`}>
                · {tone.verdict}
              </span>
              <button
                type="button"
                onClick={() => setDetailsOpen((v) => !v)}
                className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-muted/40 transition-colors shrink-0 ml-1"
                data-testid={`${testIdPrefix}-health-score-toggle`}
                aria-expanded={detailsOpen}
              >
                {detailsOpen ? 'Hide' : 'Breakdown'}
                {detailsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>
      </div>
      {detailsOpen && (
        <div className="mt-2 pt-2 border-t border-border/40 space-y-1" data-testid={`${testIdPrefix}-health-score-breakdown`}>
          {rows.map(({ key, label, part }) => {
            if (!part) {
              return (
                <div key={key} className="flex items-center justify-between text-[10px]">
                  <span className="text-muted-foreground/60">{label}</span>
                  <span className="font-mono text-muted-foreground/50 italic">no data</span>
                </div>
              );
            }
            const passing = part.score >= 12;
            return (
              <div
                key={key}
                className="flex items-center justify-between gap-2 text-[10px]"
                title={part.tip}
                data-testid={`${testIdPrefix}-health-score-row-${key}`}
              >
                <span className={`truncate ${passing ? 'text-foreground/80' : 'text-muted-foreground'}`}>
                  {label}
                </span>
                <span className={`font-mono font-semibold ${passing ? 'text-emerald-700 dark:text-emerald-400' : part.score >= 6 ? 'text-amber-700 dark:text-amber-400' : 'text-rose-700 dark:text-rose-400'}`}>
                  {Math.round(part.score)} / 20
                </span>
              </div>
            );
          })}
          <p className="pt-1 text-[10px] text-muted-foreground/80 leading-snug">
            Score = average of the {available.length} available sub-scores × 20 (each maxes out at 20 points, so 100 = perfect on every measured dimension). Hover any row for the exact number driving it.
          </p>
        </div>
      )}
    </div>
  );
}
